/**
 * IX3 Target Migration Utilities
 *
 * Handles migration from legacy scope-based targeting to new filtered targeting system.
 * Uses universal selector (*) with engine optimization for 100% auto-migration.
 */

import {
  TargetDefinition,
  ExtensionKey,
  RELATIONSHIP_TYPES,
} from '@packages/systems/ix3/core';
import {
  IX3_WF_EXTENSION_KEYS,
  TargetScope,
} from '@packages/systems/ix3-wf-plugins/shared';

export function detectTargetFormat(
  target: TargetDefinition
): 'legacy' | 'filtered' {
  return target.length === 3 ? 'filtered' : 'legacy';
}

export function shouldAutoMigrate(target: TargetDefinition): boolean {
  return detectTargetFormat(target) === 'legacy';
}

export function migrateTarget(
  legacyTarget: TargetDefinition
): TargetDefinition {
  const [extensionKey, value] = legacyTarget;

  if (!Array.isArray(value) || value.length < 2) {
    return [
      extensionKey,
      value,
      {
        relationship: RELATIONSHIP_TYPES.NONE,
        filterBy: [IX3_WF_EXTENSION_KEYS.CLASS, []],
        firstMatchOnly: false,
      },
    ];
  }

  const scope = detectScope(value[1]);

  if (!scope) {
    return [
      extensionKey,
      extractBaseSelectorFromValue(value, extensionKey),
      {
        relationship: RELATIONSHIP_TYPES.NONE,
        filterBy: [IX3_WF_EXTENSION_KEYS.CLASS, []],
        firstMatchOnly: false,
      },
    ];
  }

  const baseSelector = extractBaseSelectorFromValue(value, extensionKey);
  const filterBy: [ExtensionKey, unknown] = [extensionKey, baseSelector];

  if (Array.isArray(scope)) {
    const [scopeType, scopeSelector] = scope;
    const [relationship, firstMatchOnly] = getScopeRelationship(scopeType);
    return [
      IX3_WF_EXTENSION_KEYS.SELECTOR,
      scopeSelector,
      {relationship, filterBy, firstMatchOnly},
    ];
  }

  const [relationship, firstMatchOnly] = getScopeRelationship(scope);
  return [
    IX3_WF_EXTENSION_KEYS.ANY_ELEMENT,
    '*',
    {relationship, filterBy, firstMatchOnly},
  ];
}

/**
 * Non-functional direct scopes that were never supported by the runtime.
 * These scopes only worked when provided as array scopes with a selector.
 * When used as direct strings, they were no-ops (fell through to default case).
 *
 * TargetScope.ALL is also included because:
 * 1. It's a no-op in the runtime (falls through to default: return els)
 * 2. Migration would create any-element with relationship: 'none'
 * 3. The engine returns empty array for any-element with 'none' relationship
 * 4. The repair function doesn't fix 'none' relationship in action context
 */
const NON_FUNCTIONAL_DIRECT_SCOPES = [
  TargetScope.ALL,
  TargetScope.DESCENDANTS,
  TargetScope.ANCESTORS,
  TargetScope.FIRST_DESCENDANT,
  TargetScope.FIRST_ANCESTOR,
];

function detectScope(value: unknown): string | [string, string] | undefined {
  // Array scope format: ['descendants', '.selector'] - these work correctly
  if (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'string' &&
    typeof value[1] === 'string' &&
    isValidScope(value[0])
  ) {
    return value as [string, string];
  }

  // Direct string scope: 'children', 'parent', etc.
  if (typeof value === 'string' && isValidScope(value)) {
    // Non-functional scopes as direct strings should be treated as no-op
    if (NON_FUNCTIONAL_DIRECT_SCOPES.includes(value as TargetScope)) {
      return undefined;
    }
    return value;
  }

  return undefined;
}

function extractBaseSelectorFromValue(
  value: unknown,
  extensionKey: ExtensionKey
): unknown {
  if (!Array.isArray(value)) return value;

  if (extensionKey === IX3_WF_EXTENSION_KEYS.CLASS) {
    if (Array.isArray(value[0])) return value[0];
    // Check if second element is a scope (functional or non-functional)
    if (value.length === 2) {
      const potentialScope = value[1];
      // If it's a valid scope string (even non-functional) or array scope, extract base
      if (
        (typeof potentialScope === 'string' && isValidScope(potentialScope)) ||
        (Array.isArray(potentialScope) &&
          potentialScope.length === 2 &&
          typeof potentialScope[0] === 'string' &&
          isValidScope(potentialScope[0]))
      ) {
        return value[0];
      }
    }
    return value;
  }

  // For other extension keys
  if (value.length >= 2) {
    const potentialScope = value[1];
    // If it's a valid scope string (even non-functional) or array scope, extract base
    if (
      (typeof potentialScope === 'string' && isValidScope(potentialScope)) ||
      (Array.isArray(potentialScope) &&
        potentialScope.length === 2 &&
        typeof potentialScope[0] === 'string' &&
        isValidScope(potentialScope[0]))
    ) {
      return value[0];
    }
  }

  return value;
}

function getScopeRelationship(
  scope: string
): [RELATIONSHIP_TYPES, firstMatchOnly: boolean] {
  switch (scope) {
    case TargetScope.CHILDREN:
      return [RELATIONSHIP_TYPES.DIRECT_CHILD_OF, false];
    case TargetScope.PARENT:
      return [RELATIONSHIP_TYPES.DIRECT_PARENT_OF, false];
    case TargetScope.NEXT:
      return [RELATIONSHIP_TYPES.NEXT_SIBLING_OF, false];
    case TargetScope.PREVIOUS:
      return [RELATIONSHIP_TYPES.PREV_SIBLING_OF, false];
    case TargetScope.SIBLINGS:
      return [RELATIONSHIP_TYPES.NEXT_TO, false];
    case TargetScope.DESCENDANTS:
      return [RELATIONSHIP_TYPES.WITHIN, false];
    case TargetScope.FIRST_DESCENDANT:
      return [RELATIONSHIP_TYPES.WITHIN, true];
    case TargetScope.ANCESTORS:
      return [RELATIONSHIP_TYPES.CONTAINS, false];
    case TargetScope.FIRST_ANCESTOR:
      return [RELATIONSHIP_TYPES.CONTAINS, true];
    default:
      return [RELATIONSHIP_TYPES.NONE, false];
  }
}

function isValidScope(value: unknown): value is TargetScope {
  switch (value) {
    case TargetScope.ALL:
    case TargetScope.CHILDREN:
    case TargetScope.SIBLINGS:
    case TargetScope.PARENT:
    case TargetScope.NEXT:
    case TargetScope.PREVIOUS:
    case TargetScope.DESCENDANTS:
    case TargetScope.ANCESTORS:
    case TargetScope.FIRST_DESCENDANT:
    case TargetScope.FIRST_ANCESTOR:
      return true;
    default:
      return false;
  }
}

/**
 * Migrates wf:id targets to wf:selector targets with # prefix.
 * Handles simple IDs, IDs with scope, and preserves filtering context.
 *
 * @param target - The target definition to migrate
 * @returns Migrated target definition with wf:selector or original if not wf:id
 *
 * @example
 * // Simple ID
 * migrateIdToSelector(['wf:id', 'my-element'])
 * // => ['wf:selector', '#my-element']
 *
 * @example
 * // ID with scope
 * migrateIdToSelector(['wf:id', ['button-id', 'parent']])
 * // => ['wf:selector', ['#button-id', 'parent']]
 *
 * @example
 * // ID with filtering context
 * migrateIdToSelector(['wf:id', 'elem', {relationship: 'within', filterBy: [...], firstMatchOnly: false}])
 * // => ['wf:selector', '#elem', {relationship: 'within', filterBy: [...], firstMatchOnly: false}]
 */
export function migrateIdToSelector(
  target: TargetDefinition
): TargetDefinition {
  // Not a wf:id target, return as-is
  if (target[0] !== IX3_WF_EXTENSION_KEYS.ID) {
    return target;
  }

  const [_extensionKey, value, filterContext] = target;

  // Helper to add # prefix if not already present
  const addHashPrefix = (id: string): string => {
    if (typeof id !== 'string') return id;
    const trimmed = id.trim();
    if (trimmed === '') return trimmed;
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  };

  // Handle different value formats
  let migratedValue: unknown;

  if (typeof value === 'string') {
    // Simple case: ['wf:id', 'my-id'] => ['wf:selector', '#my-id']
    migratedValue = addHashPrefix(value);
  } else if (Array.isArray(value)) {
    if (value.length === 2) {
      const [idValue, scope] = value;

      // Check if this is [id, scope] format
      if (typeof idValue === 'string' && typeof scope === 'string') {
        // ['wf:id', ['my-id', 'parent']] => ['wf:selector', ['#my-id', 'parent']]
        migratedValue = [addHashPrefix(idValue), scope];
      } else if (
        typeof idValue === 'string' &&
        Array.isArray(scope) &&
        scope.length === 2
      ) {
        // ['wf:id', ['my-id', ['descendants', '.btn']]] => ['wf:selector', ['#my-id', ['descendants', '.btn']]]
        migratedValue = [addHashPrefix(idValue), scope];
      } else {
        // Unknown format, preserve as-is
        migratedValue = value;
      }
    } else {
      // Unknown array format, preserve as-is
      migratedValue = value;
    }
  } else {
    // Unknown value type, preserve as-is
    migratedValue = value;
  }

  // Construct migrated target
  if (filterContext) {
    return [IX3_WF_EXTENSION_KEYS.SELECTOR, migratedValue, filterContext];
  }

  return [IX3_WF_EXTENSION_KEYS.SELECTOR, migratedValue];
}

/**
 * Repairs incorrectly migrated targets caused by the non-functional scope bug.
 *
 * When scopes like 'descendants' or 'ancestors' were used as direct strings
 * (not array scopes), they were no-ops in the runtime but the migration treated
 * them as functional, creating wf:any-element targets that behave differently.
 *
 * This function detects and repairs those patterns:
 * - For triggers: ALL wf:any-element targets (shouldn't exist in UI)
 * - For actions: wf:any-element with 'within' or 'contains' relationships
 *
 * @param target - The target definition to check and potentially repair
 * @param context - Whether this is a trigger or action target
 * @returns Repaired target definition, or original if no repair needed
 *
 * @example
 * // Incorrectly migrated trigger target
 * repairIncorrectlyMigratedTarget(
 *   ['wf:any-element', '*', {
 *     relationship: 'within',
 *     filterBy: ['wf:class', ['my-class']],
 *     firstMatchOnly: false
 *   }],
 *   'trigger'
 * )
 * // => ['wf:class', ['my-class'], {relationship: 'none', filterBy: [...], firstMatchOnly: false}]
 */
export function repairIncorrectlyMigratedTarget(
  target: TargetDefinition,
  context: 'trigger' | 'action'
): TargetDefinition {
  // Only repair 3-tuple targets
  if (target.length !== 3) return target;

  const [extensionKey, value, filterContext] = target;

  // Only repair wf:any-element targets
  if (extensionKey !== IX3_WF_EXTENSION_KEYS.ANY_ELEMENT) return target;
  if (value !== '*') return target;
  if (!filterContext || typeof filterContext !== 'object') return target;

  const {relationship, filterBy} = filterContext as {
    relationship?: string;
    filterBy?: TargetDefinition;
    [key: string]: unknown;
  };

  if (!filterBy || !Array.isArray(filterBy) || filterBy.length < 2) {
    return target;
  }

  // Determine if we should repair based on context and relationship
  //
  // Only repair in TRIGGER context because:
  // - The UI never allowed any-element selection for triggers (shouldShow: false)
  // - So ALL trigger any-element targets came from migration
  // - We can safely repair 'within'/'contains' since users couldn't have created them
  //
  // Do NOT repair in ACTION context because:
  // - The UI allows any-element selection with any filter relationship
  // - Users could have intentionally created any-element with 'within' filter
  // - We would destroy valid user-created data
  //
  // Only repair 'within' and 'contains' relationships - these came from
  // non-functional scopes ('descendants', 'ancestors', etc.) that were no-ops.
  const shouldRepair =
    context === 'trigger' &&
    (relationship === 'within' || relationship === 'contains');

  if (!shouldRepair) return target;

  // Repair: Extract original target from filterBy and reset relationship to none
  const [filterKey, filterValue] = filterBy;
  return [
    filterKey,
    filterValue,
    {
      relationship: RELATIONSHIP_TYPES.NONE,
      filterBy: [IX3_WF_EXTENSION_KEYS.CLASS, []],
      firstMatchOnly: false,
    },
  ];
}

/**
 * Repairs targets where filterContext exists but filterBy is missing.
 * This handles corrupt data from MongoDB where filterContext was written
 * without the required filterBy field (e.g., via snapshot restore or
 * direct DB operations that bypass application validation).
 *
 * Since relationship "none" with no filterBy is semantically "no filter,"
 * the repair supplies a default filterBy rather than removing filterContext,
 * so the 3-tuple format is preserved for consistency.
 *
 * Also clears `firstMatchOnly: true` when relationship is `none` — Designer
 * never authors that combo (clear-on-none), and DE/MCP write guards reject it.
 * Leaving the flag sticky would make a later full-replace update hard-fail
 * after this repair filled in filterBy (or for already-complete corrupt rows).
 */
export function repairMissingFilterBy(
  target: TargetDefinition
): TargetDefinition {
  if (target.length !== 3) return target;

  const [extensionKey, value, filterContext] = target;

  if (!filterContext || typeof filterContext !== 'object') return target;

  const fc = filterContext as {
    relationship?: RELATIONSHIP_TYPES;
    filterBy?: unknown;
    firstMatchOnly?: boolean;
  };

  if (fc.filterBy !== undefined && fc.filterBy !== null) {
    if (
      fc.relationship === RELATIONSHIP_TYPES.NONE &&
      fc.firstMatchOnly === true
    ) {
      return [
        extensionKey,
        value,
        {
          relationship: RELATIONSHIP_TYPES.NONE,
          filterBy: fc.filterBy as [ExtensionKey, unknown],
          firstMatchOnly: false,
        },
      ];
    }
    return target;
  }

  const relationship = fc.relationship ?? RELATIONSHIP_TYPES.NONE;
  return [
    extensionKey,
    value,
    {
      relationship,
      filterBy: [IX3_WF_EXTENSION_KEYS.CLASS, []] as [ExtensionKey, unknown],
      firstMatchOnly:
        relationship === RELATIONSHIP_TYPES.NONE
          ? false
          : (fc.firstMatchOnly ?? false),
    },
  ];
}

/**
 * Recursively migrates wf:id targets in filterBy contexts to wf:selector.
 * Handles nested filtering contexts and preserves relationship metadata.
 *
 * @param target - The target definition to check and migrate
 * @returns Target definition with filterBy contexts migrated
 *
 * @example
 * // Migrate filterBy context
 * migrateFilterByRecursive(['wf:selector', '*', {
 *   relationship: 'direct-child-of',
 *   filterBy: ['wf:id', 'container'],
 *   firstMatchOnly: false
 * }])
 * // => ['wf:selector', '*', {
 * //   relationship: 'direct-child-of',
 * //   filterBy: ['wf:selector', '#container'],
 * //   firstMatchOnly: false
 * // }]
 */
export function migrateFilterByRecursive(
  target: TargetDefinition
): TargetDefinition {
  // Check if target has filtering context (3-tuple format)
  if (target.length !== 3) {
    return target;
  }

  const [extensionKey, value, filterContext] = target;

  // Check if filterContext has filterBy property
  if (
    !filterContext ||
    typeof filterContext !== 'object' ||
    !('filterBy' in filterContext)
  ) {
    return target;
  }

  const {filterBy} = filterContext as {
    filterBy?: TargetDefinition;
    [key: string]: unknown;
  };

  if (!filterBy || !Array.isArray(filterBy)) {
    return target;
  }

  // Migrate the filterBy target if it's wf:id
  let migratedFilterBy = filterBy;
  if (filterBy[0] === IX3_WF_EXTENSION_KEYS.ID) {
    migratedFilterBy = migrateIdToSelector(filterBy);
  }

  // Recursively migrate nested filterBy contexts
  migratedFilterBy = migrateFilterByRecursive(migratedFilterBy);

  return [
    extensionKey,
    value,
    {...filterContext, filterBy: [migratedFilterBy[0], migratedFilterBy[1]]},
  ];
}
